import { ReactNode, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleExclamation, faEnvelopeOpen, faKey, faMobile } from "@fortawesome/pro-light-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import userAppLogo from "../image/user-app.svg";
import DeepLinkLanguageSwitcher from "./DeepLinkLanguageSwitcher";
import { resolveLanguage } from "../i18n";
import { useChangeLanguage } from "../containers/Topbar/useLanguageMenu";
import { showHtml } from "../utils/showHtml";

type DeepLinkStepCardProps = {
  title: string;
  bullets?: string[];
  icon: IconDefinition;
  renderFooter?: ReactNode;
  compact?: boolean;
};

function DeepLinkStepCard({ title, bullets, icon, renderFooter, compact }: DeepLinkStepCardProps) {
  return (
    <div
      className={`bg-white rounded-xl p-6 shadow-md w-full max-w-[420px] ${compact ? "md:w-[340px] xxl:w-[380px]" : "md:w-[420px]"}`}
    >
      <div className="flex items-center gap-4 mb-4">
        <div className="relative">
          <div
            className="rounded-full flex items-center justify-center py-4 px-3 text-primary"
            style={{ backgroundColor: "#E6F7EF", borderRadius: "50%" }}
          >
            <FontAwesomeIcon aria-hidden="true" icon={icon} size="xl" />
          </div>
        </div>
        <div className="text-lg font-semibold text-gray-900">{title}</div>
      </div>
      {bullets && bullets.length > 0 && (
        <ul className="text-[#6E6E6E] font-medium text-base mb-4 pl-5 space-y-1 list-disc">
          {bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
      )}
      {renderFooter}
    </div>
  );
}

export default function DeepLink() {
  const { lang } = useParams<{ lang: string }>();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const changeLanguage = useChangeLanguage();
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [platform, setPlatform] = useState<"android" | "ios" | "desktop">("desktop");

  useEffect(() => {
    if (lang) {
      const resolved = resolveLanguage(lang as any);
      if (resolved !== i18n.language) {
        changeLanguage(resolved);
      }
    }
  }, [lang]);

  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || "";
    if (/android/i.test(userAgent)) {
      setPlatform("android");
      return;
    }

    if (/iPad|iPhone|iPod/.test(userAgent)) {
      setPlatform("ios");
      return;
    }

    setPlatform("desktop");
  }, []);

  const code = searchParams.get("code");
  const hasCode = !!code;
  const isValidCode = !!code && /^[a-zA-Z0-9]{12}$/.test(code);
  const isInvalidCode = hasCode && !isValidCode;
  const showCodeInHeader = isInvalidCode && code.length <= 20;
  const isAnonymous = searchParams.get("anonymous") === "1";
  const deepLinkQuery = isAnonymous ? "anonymous=1" : isValidCode ? `code=${code}` : "";
  const useExpandedLayout = !isValidCode && !isAnonymous;

  const androidUrl = deepLinkQuery
    ? `https://play.google.com/store/apps/details?id=com.emz.egate.digi.userapp&referrer=${encodeURIComponent(deepLinkQuery)}`
    : "https://play.google.com/store/apps/details?id=com.emz.egate.digi.userapp";
  const iosUrl = "https://apps.apple.com/app/egate-digi/id1497026032";

  const copyButtonText = copied ? t("Copied!") : copyFailed ? t("Failed") : t("Copy");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code!);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setCopyFailed(true);
      setTimeout(() => setCopyFailed(false), 2000);
    }
  };

  const showAndroidButton = platform === "android" || platform === "desktop";
  const showIosButton = platform === "ios" || platform === "desktop";

  const storeButtons = (
    <div className="flex gap-2 items-center">
      {showAndroidButton && (
        <a href={androidUrl} rel="noopener noreferrer" target="_blank">
          <img
            alt={t("Get it on Google Play")}
            className="h-10"
            src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
          />
        </a>
      )}
      {showIosButton && (
        <a href={iosUrl} rel="noopener noreferrer" target="_blank">
          <img
            alt={t("Download on the App Store")}
            className="h-7"
            src="https://developer.apple.com/app-store/marketing/guidelines/images/badge-download-on-the-app-store.svg"
          />
        </a>
      )}
    </div>
  );

  const steps = [
    {
      title: t("Install the app"),
      bullets: [t("Download the eGate Digi app"), t("Available on Google Play and the App Store")],
      icon: faMobile,
      renderFooter: storeButtons,
    },
    isAnonymous
      ? {
          title: t("Use anonymous mode"),
          bullets: [t('Open the app and tap "Continue in anonymous mode"'), t("No contract code needed")],
          icon: faKey,
          renderFooter:
            platform !== "desktop" ? (
              <div>
                <a
                  className="bg-primary text-white border-none py-3 px-6 rounded-lg font-bold cursor-pointer no-underline block text-center"
                  href={`emz-userapp://activate-contract?${deepLinkQuery}`}
                >
                  {t("Open in app")}
                </a>
              </div>
            ) : undefined,
        }
      : isValidCode
        ? {
            title: t("Enter activation code"),
            bullets: [t("Use this activation code to register"), t("Enter it during account creation")],
            icon: faKey,
            renderFooter: (
              <>
                <div
                  className="flex w-full items-center rounded-[10px] overflow-hidden"
                  style={{ backgroundColor: "#F9FAFB", padding: "7px" }}
                >
                  <div className="flex-1 min-w-0 text-gray-900 px-6 py-3 font-bold text-lg text-center uppercase truncate">{code}</div>
                  <button
                    className="shrink-0 whitespace-nowrap bg-primary text-white border-none px-6 py-3 font-bold uppercase cursor-pointer rounded-[10px] transition-opacity hover:opacity-90"
                    onClick={handleCopy}
                  >
                    {copyButtonText}
                  </button>
                </div>
                {platform !== "desktop" && (
                  <div className="mt-4">
                    <a
                      className="bg-primary text-white border-none py-3 px-6 rounded-lg font-bold cursor-pointer no-underline block text-center"
                      href={`emz-userapp://activate-contract?${deepLinkQuery}`}
                    >
                      {t("Open in app")}
                    </a>
                  </div>
                )}
              </>
            ),
          }
        : isInvalidCode
          ? {
              title: t("Get a valid code"),
              bullets: [
                t("The provided code is not valid"),
                t("Contact your service provider or municipality for a correct code"),
                t("The code contains 12 characters (e.g. AB12CD34EF56)"),
              ],
              icon: faEnvelopeOpen,
            }
          : {
              title: t("Prepare your contract code"),
              bullets: [
                t("Have your contract code ready"),
                t("The code contains 12 characters"),
                t("You will enter it during registration"),
                t("Provided by your service provider or municipality"),
              ],
              icon: faKey,
            },
    ...(isInvalidCode
      ? [
          {
            title: t("Enter code manually"),
            bullets: [t("Open the app and enter the code when prompted during registration")],
            icon: faKey,
          },
          {
            title: t("You are all set!"),
            bullets: [
              t("Your account will be activated after entering a valid code"),
              t("You can then use the app to open containers"),
            ],
            icon: faCircleCheck,
          },
        ]
      : useExpandedLayout
        ? [
            {
              title: t("Find your code"),
              bullets: [
                t("Check your email or letter from your service provider or municipality"),
                t("If you cannot find the code, contact your provider"),
                t("Registration is required unless anonymous access is supported"),
              ],
              icon: faEnvelopeOpen,
            },
            {
              title: t("You are all set!"),
              bullets: [
                t("Enter the contract code during registration"),
                t("Your account will be activated"),
                t("You can then use the app to open containers"),
              ],
              icon: faCircleCheck,
            },
          ]
        : [
            {
              title: t("You are all set!"),
              bullets: [
                ...(isAnonymous ? [] : [t("Your contract is now linked")]),
                t("You can use the app to open containers"),
              ],
              icon: faCircleCheck,
            },
          ]),
  ];

  return (
    <>
      {/* Tailwind CDN */}
      <script src="https://cdn.tailwindcss.com"></script>

      <div
        className="min-h-screen bg-white"
        style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" }}
      >
        {/* Language Switch */}
        <div className="absolute top-4 right-4 z-10">
          <DeepLinkLanguageSwitcher />
        </div>

        {/* Hero Section */}
        <section className="text-white py-20 px-5 bg-primary">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row justify-between items-center">
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-4xl md:text-5xl font-extrabold mb-6 leading-tight">
                {t("Manage your waste with eGate Digi")}
              </h1>
              <p className="text-xl font-medium mb-8">
                {isAnonymous
                  ? t("Continue in anonymous mode from the app login screen.")
                  : isValidCode
                    ? t("Use the code below during registration to activate your account!")
                    : ""}
              </p>
              {isValidCode && !isAnonymous && (
                <div className="inline-flex bg-white rounded-[10px] overflow-hidden p-1">
                  <div className="text-gray-900 px-6 py-3 font-bold text-lg min-w-[120px] text-center uppercase">
                    {code}
                  </div>
                  <button
                    className="shrink-0 whitespace-nowrap bg-primary text-white border-none px-6 py-3 font-bold uppercase cursor-pointer rounded-[10px] transition-opacity hover:opacity-90"
                    onClick={handleCopy}
                  >
                    {copyButtonText}
                  </button>
                </div>
              )}
              {isInvalidCode && (
                <div
                  className="inline-flex flex-col bg-white rounded-[10px] overflow-hidden border-2"
                  style={{ borderColor: "#ef4444" }}
                >
                  {showCodeInHeader && (
                    <div className="px-6 py-3 text-gray-900 font-bold text-lg text-center uppercase min-w-[120px]">
                      {code}
                    </div>
                  )}
                  <div
                    className="flex items-center gap-2 px-3 py-2.5 text-sm"
                    style={{
                      backgroundColor: "#fef2f2",
                      borderTop: showCodeInHeader ? "1px solid #fee2e2" : undefined,
                      color: "#b91c1c",
                    }}
                  >
                    <FontAwesomeIcon className="shrink-0" icon={faCircleExclamation} />
                    <span>{t("Invalid code. Please contact your service provider.")}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="flex-1 hidden md:flex justify-end">
              <div className="inline-block bg-white rounded-full" style={{ padding: "6px" }}>
                <img alt={t("eGate Digi")} className="w-64 h-auto block" src={userAppLogo} />
              </div>
            </div>
          </div>
        </section>

        {/* Steps Section */}
        <section className="py-8 px-5 bg-gray-50">
          <div className={`${useExpandedLayout ? "max-w-[1700px]" : "max-w-[1300px]"} mx-auto`}>
            <h2 className="text-3xl font-bold mb-6 text-center">{t("How it works?")}</h2>

            <div
              className={`grid grid-cols-1 justify-items-center ${useExpandedLayout ? "gap-4 md:grid-cols-2 xl:grid-cols-4" : "gap-8 xl:grid-cols-3"}`}
            >
              {steps.map((step) => (
                <DeepLinkStepCard
                  key={step.title}
                  bullets={step.bullets}
                  compact={useExpandedLayout}
                  icon={step.icon}
                  renderFooter={step.renderFooter}
                  title={step.title}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-4 px-6 flex gap-2">
          <button
            className="text-[#6E6E6E] underline cursor-pointer bg-transparent border-none text-[20px] font-extralight p-0"
            onClick={() => showHtml("legalnotice")}
          >
            {t("Legal notice")}
          </button>
          <button
            className="text-[#6E6E6E] underline cursor-pointer bg-transparent border-none text-[20px] font-extralight p-0"
            onClick={() => showHtml("privacypolicy")}
          >
            {t("Privacy policy")}
          </button>
          <button
            className="text-[#6E6E6E] underline cursor-pointer bg-transparent border-none text-[20px] font-extralight p-0"
            onClick={() => showHtml("eula")}
          >
            {t("EULA")}
          </button>
        </footer>
      </div>
    </>
  );
}
